import {
	HttpClient,
	HttpParameterCodec,
	HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
	IContact,
	IContactFilters,
	IContactList,
	IContactListResponse,
	IContactStatus,
	IRecurrencyData,
} from 'app/interfaces/contact.interface';
import { ICalendarEvent, IEventList } from 'app/interfaces/event.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserService } from './user.service';

class HttpUrlEncodingCodec implements HttpParameterCodec {
	encodeKey(k: string): string {
		return encodeURIComponent(k);
	}
	encodeValue(k: string): string {
		return encodeURIComponent(k);
	}
	decodeKey(k: string): string {
		return decodeURIComponent(k);
	}
	decodeValue(v: string) {
		return decodeURIComponent(v);
	}
}

@Injectable()
export class ContactsService {
	collectionName = 'contacts';
	// User preferences.
	userPref: IUserPreferences;
	defaultImage = '/assets/images/nophoto_blue_solid.png';
	// Constructor method.
	constructor(
		protected http: HttpClient,
		protected translate: TranslateService,
		protected user: UserService
	) {}

	public patchContact(value) {
		const url = environment.servicesUrl;
		return this.http.patch<any>(`${url}/professional/contact`, value);
	}

	// Get contacts list.

	public getContacts(
		search = '',
		limit = 30,
		skip = 0,
		sort = 'displayName asc',
		filters: IContactFilters = null,
		professionalType = ''
	): Observable<IContactListResponse> {
		const url = environment.servicesUrl;
		// Parameters.
		let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
		params = params.set('limit', limit.toString());
		params = params.set('sort', sort);
		params = params.set('skip', skip.toString());
		params = params.set('search', search);

		for (const filter in filters) {
			if (filters[filter] && filter !== 'levels') {
				params = params.set(filter, filters[filter]);
			}
		}

		if (filters?.levels.length > 0) {
			params = params.set(
				'type',
				professionalType === 'psychologist' ||
					professionalType === 'psychiatrist'
					? 'cgi'
					: 'life_quality'
			);

			filters.levels.forEach((level) => {
				params = params.append('levels', level);
			});
		}

		return this.http.get<IContactListResponse>(`${url}/professional/contact`, {
			params,
		});
	}

	// Get contact by id
	public getContact(id: number): Observable<IContact> {
		return this.http.get<IContact>(`contacts/${id}`);
	}

	// Get name of a contact.
	public getContactName(id: number): Observable<string> {
		return this.getContact(id).pipe(
			map((res) => {
				return res.displayName;
			})
		);
	}

	// Build a list of contacts.
	public buildContactList(contacts: IContact[]): IContactList[] {
		const noNameFound = this.translate.instant('PAGES.CLIENTS.NO_NAME');
		return contacts.map((item) => {
			return {
				id: item.id,
				lastEventDate: item.lastEventDate ? new Date(item.lastEventDate) : null,
				displayName: item.displayName || noNameFound,
				email: item.email || '',
				photoUrl: item.photoUrl || this.defaultImage,
				profileId: item.profileId,
				refUserId: item.refUserId || '',
				refProfessionalId: item.refProfessionalId,
				invitedByProfessional: item.invitedByProfessional,
				status: this.getStatusText(item.status),
			} as IContactList;
		});
	}

	// Save a new contact.
	public saveContact(contact: IContact): Observable<IContact> {
		return this.http.post<IContact>('contacts', JSON.stringify(contact));
	}

	// update a contact.
	public updateContact(contact: IContact): Observable<IContact> {
		// Build object for update.
		const upObj: Partial<IContact> = {
			address: contact.address || null,
			address1: contact.address1 || null,
			city: contact.city || null,
			country: contact.country || null,
			email: contact.email || null,
			displayName: contact.displayName,
			fiscalId: contact.fiscalId || null,
			iCD: contact.iCD || null,
			phoneFix: contact.phoneFix || null,
			phoneMobile: contact.phoneMobile || null,
			postalCode: contact.postalCode || null,
			sessionPrice: contact.sessionPrice || null,
			sessionPriceHalfHour: contact.sessionPriceHalfHour || null,
			status: contact.status || null,
			notes: contact.notes || null,
		};

		// Try to update.
		return this.http.patch<IContact>(
			`contacts/${contact.id}`,
			JSON.stringify(upObj)
		);
	}

	public deleteContactById(contactId: string): Observable<undefined> {
		return this.http.delete<undefined>(`contacts/${contactId}`);
	}

	// Send an invite to an user.
	public inviteUser(userId: number): Observable<boolean> {
		return this.http.post<boolean>(`contacts/${userId}/invite`, null);
	}

	// Return status.
	public getStatusList(): IContactStatus[] {
		const list: IContactStatus[] = [];
		list.push({
			key: 'active',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.ACTIVE'),
		});
		list.push({
			key: 'finish',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.COMPLETED'),
		});
		list.push({
			key: 'contact_after',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.CONTACTLATER'),
		});
		list.push({
			key: 'quit',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.GIVEUP'),
		});
		list.push({
			key: 'new',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.NEW'),
		});
		list.push({
			key: 'holidays',
			text: this.translate.instant('PAGES.CLIENTS.STATUSLIST.VACATION'),
		});
		return list;
	}

	// Return status text.
	public getStatusText(key: string): string {
		let status = 'NEW';
		if (key) {
			switch (key.toLowerCase()) {
				case 'active':
					status = 'ACTIVE';
					break;
				case 'finish':
					status = 'COMPLETED';
					break;
				case 'contact_after':
					status = 'CONTACTLATER';
					break;
				case 'quit':
					status = 'GIVEUP';
					break;
				case 'new':
					status = 'NEW';
					break;
				case 'holidays':
					status = 'VACATION';
					break;
				case 'default':
					status = 'NEW';
					break;
				case 'guide':
					status = 'GUIDE';
					break;
			}
		}
		return this.translate.instant('PAGES.CLIENTS.STATUSLIST.' + status);
	}

	// Get contact events.
	public getEvents(
		contactId: number,
		limit = 20,
		skip = 0
	): Observable<IEventList> {
		// Parameters.
		let params = new HttpParams();
		params = params.set('limit', limit.toString());
		if (skip > 0) {
			params = params.set('skip', skip.toString());
		}

		return this.http.get<IEventList>(`appointments/contact/${contactId}`, {
			params,
		});
	}

	// TODO find a better way to load user images
	public async getContactImage(event: ICalendarEvent): Promise<string> {
		return event.userImageUrl;
	}

	public getContactRecurrencyData(contactId) {
		const url = `${environment.servicesUrl}/professional/contact`;
		return this.http.get<IRecurrencyData>(
			`${url}/${contactId}/recurring-schedules`
		);
	}
}
